import { Modal } from '@shared/components';
import { ResponseDraftContext } from '@shared/contexts/ResponseDraftContext';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { useContext, useEffect, useState } from 'react';
import { OriginatorAutoReplySettings } from '../OriginatorAutoReplySettings/OriginatorAutoReplySettings';
import { UserAutoReplySettings } from '../UserAutoReplySettings/UserAutoReplySettings';

const ROOT = makeRootClassName('AutoReplySettingsModal');
const el = makeElementClassNameFactory(ROOT);

export enum AutoReplySettingsLevel {
  USER = 'USER',
  ORGANIZATION = 'ORGANIZATION',
  TEAM = 'TEAM',
  ORIGINATOR = 'ORIGINATOR',
}

type AutoReplySettingsModalProps = {
  open: boolean;
  setOpen: (val: boolean) => void;
  quoteIds: string[];
  originator: {
    id?: string;
    name?: string;
  };
};

const getSettingsLevelLabel = (settingsLevel: AutoReplySettingsLevel) => {
  switch (settingsLevel) {
    case AutoReplySettingsLevel.USER:
      return 'User';
    case AutoReplySettingsLevel.ORGANIZATION:
      return 'Organization';
    case AutoReplySettingsLevel.TEAM:
      return 'Team';
    case AutoReplySettingsLevel.ORIGINATOR:
      return 'Customer';
  }
};

export const AutoReplySettingsModal = ({
  open,
  setOpen,
  quoteIds,
  originator,
}: AutoReplySettingsModalProps) => {
  const { refetchDraft } = useContext(ResponseDraftContext);
  const [settingsLevel, setSettingsLevel] = useState(
    AutoReplySettingsLevel.ORIGINATOR
  );
  useEffect(() => {
    if (!open) {
      refetchDraft?.();
    }
  }, [open]);

  const handleChangeLevel = (level: AutoReplySettingsLevel) => () => {
    setSettingsLevel(level);
  };

  const settingsContainer = () => {
    switch (settingsLevel) {
      case AutoReplySettingsLevel.USER:
        return (
          <UserAutoReplySettings
            quoteIds={quoteIds}
            originatorName={originator?.name}
            handleClose={() => setOpen(false)}
            handleChangeLevel={handleChangeLevel(
              AutoReplySettingsLevel.ORIGINATOR
            )}
          />
        );
      case AutoReplySettingsLevel.ORGANIZATION:
        throw new Error('Organization settings not implemented');
      case AutoReplySettingsLevel.TEAM:
        throw new Error('Team settings not implemented');
      case AutoReplySettingsLevel.ORIGINATOR:
        return (
          <OriginatorAutoReplySettings
            quoteIds={quoteIds}
            originatorId={originator?.id}
            handleClose={() => setOpen(false)}
            handleChangeLevel={handleChangeLevel(AutoReplySettingsLevel.USER)}
          />
        );
      default:
        throw new Error('Invalid settings level');
    }
  };

  return (
    <Modal.Root className={ROOT} open={open} onOpenChange={setOpen}>
      <Modal.Content size="responsive-takeover">
        <Modal.Header
          className={el`header`}
          title={`${getSettingsLevelLabel(settingsLevel)} Auto-reply Settings`}
          subtitle={'Drafting instructions'}
          hasCloseButton
          onClose={() => setOpen(false)}
        />
        {settingsContainer()}
      </Modal.Content>
    </Modal.Root>
  );
};
